import produce from "immer";
import { handleActions } from "redux-actions";

import { TransactionState } from "../../interfaces";
import { mapTransactionFromPayload } from "../../utils/transaction";
import { METHOD_TYPE } from "../../enums";
import { LOG_OUT } from "../system/actions";
import { SET_OLDEST_TRANSACTION_YEAR } from "./actions";
import {
  START,
  FAIL,
  SUCCESS,
  FETCH_ITEMS,
  DELETE_ITEMS,
  CREATE_ITEM,
  DELETE_ITEM,
  UPDATE_ITEM,
  RESET,
  TRANSACTION,
  SELECT_ITEMS,
  FS_CREATE_ITEMS,
  FS_DELETE_ITEMS,
  FS_UPDATE_ITEMS,
  CLEAR_UPDATE_METHOD,
} from "../common";

const initialState: TransactionState = {
  itemsById: {},
  total: 0,
  start: 1,
  end: 1,
  limit: 25,

  method: METHOD_TYPE.LIST,
  loading: false,
  loaded: false,
  error: null,
  message: "",

  loadingAccounts: false,
  oldestYear: null,

  selected: [],
  filter: {
    items: [],
  },
};

export const transactionReducer = handleActions<TransactionState, any>(
  {
    [TRANSACTION + RESET]: () => initialState,
    [TRANSACTION + FETCH_ITEMS + START]: (state, { payload: { start } }) =>
      produce(state, (draft) => {
        draft.start = start || initialState.start;
        draft.method = METHOD_TYPE.LIST;
        draft.loading = true;
        draft.error = null;
      }),
    [TRANSACTION + FETCH_ITEMS + SUCCESS]: (
      state,
      { payload: { data, length, total } }
    ) =>
      produce(state, (draft) => {
        draft.loading = false;
        draft.loaded = false;
        const next: Record<string, any> = { ...draft.itemsById };
        for (const e of data as any[]) {
          const mapped = mapTransactionFromPayload(e);
          if (mapped.id) next[mapped.id] = mapped;
        }
        draft.itemsById = next;
        draft.total = total;
        draft.error = null;
      }),
    [TRANSACTION + FETCH_ITEMS + FAIL]: (state, { payload }) =>
      produce(state, (draft) => {
        draft.loading = false;
        draft.error = payload;
      }),
    [TRANSACTION + DELETE_ITEMS + START]: (state) =>
      produce(state, (draft) => {
        draft.method = METHOD_TYPE.DELETE_ALL;
        draft.loading = true;
        draft.loaded = false;
      }),
    [TRANSACTION + DELETE_ITEMS + SUCCESS]: (state, { payload }) =>
      produce(state, (draft) => {
        draft.loading = false;
        draft.loaded = true;
        draft.itemsById = {};
      }),
    [TRANSACTION + DELETE_ITEMS + FAIL]: (state, { payload }) =>
      produce(state, (draft) => {
        draft.loading = false;
        draft.error = payload;
      }),
    [TRANSACTION + CREATE_ITEM + START]: (state) =>
      produce(state, (draft) => {
        draft.method = METHOD_TYPE.CREATE;
        draft.loading = true;
        draft.error = null;
        draft.loaded = false;
      }),
    [TRANSACTION + CREATE_ITEM + SUCCESS]: (state, { payload }) =>
      produce(state, (draft) => {
        draft.loading = false;
        draft.loaded = true;
        draft.error = null;
      }),
    [TRANSACTION + CREATE_ITEM + FAIL]: (state, { payload }) =>
      produce(state, (draft) => {
        draft.loading = false;
        draft.error = payload;
      }),

    [TRANSACTION + UPDATE_ITEM + START]: (state) =>
      produce(state, (draft) => {
        draft.method = METHOD_TYPE.UPDATE;
        draft.loading = true;
        draft.error = null;
      }),
    [TRANSACTION + UPDATE_ITEM + SUCCESS]: (state, { payload }) =>
      produce(state, (draft) => {
        if (payload?.id) {
          draft.itemsById[payload.id] = mapTransactionFromPayload(payload);
        }
        draft.loading = false;
        draft.loaded = false;
        draft.error = null;
      }),
    [TRANSACTION + UPDATE_ITEM + FAIL]: (state, { payload }) =>
      produce(state, (draft) => {
        draft.loading = false;
        draft.error = payload;
      }),

    [TRANSACTION + DELETE_ITEM + START]: (state) =>
      produce(state, (draft) => {
        draft.method = METHOD_TYPE.DELETE;
        draft.loading = true;
        draft.error = null;
      }),
    [TRANSACTION + DELETE_ITEM + SUCCESS]: (state, { payload }) =>
      produce(state, (draft) => {
        delete draft.itemsById[payload];
        draft.loading = false;
        draft.loaded = false;
        draft.error = null;
      }),
    [TRANSACTION + DELETE_ITEM + FAIL]: (state, { payload }) =>
      produce(state, (draft) => {
        draft.loading = false;
        draft.error = payload;
      }),
    [TRANSACTION + SELECT_ITEMS]: (state, { payload }) =>
      produce(state, (draft) => {
        draft.selected = payload;
      }),
    [CLEAR_UPDATE_METHOD]: (state, { payload }) =>
      produce(state, (draft) => {
        draft.method = METHOD_TYPE.LIST;
      }),
    [SET_OLDEST_TRANSACTION_YEAR]: (state, { payload }) =>
      produce(state, (draft) => {
        draft.oldestYear = typeof payload === "number" ? payload : null;
      }),
    [TRANSACTION + FS_CREATE_ITEMS]: (state, { payload }) =>
      produce(state, (draft) => {
        for (const e of (payload || []) as any[]) {
          const mapped = mapTransactionFromPayload(e);
          if (mapped.id) draft.itemsById[mapped.id] = mapped;
        }
        draft.loading = false;
        draft.loaded = true;
      }),
    [TRANSACTION + FS_UPDATE_ITEMS]: (state, { payload }) =>
      produce(state, (draft) => {
        for (const updated of payload) {
          const mapped = mapTransactionFromPayload(updated);
          if (mapped.id) draft.itemsById[mapped.id] = mapped;
        }
      }),
    [TRANSACTION + FS_DELETE_ITEMS]: (state, { payload }) =>
      produce(state, (draft) => {
        for (const deleted of payload) {
          if (deleted?.id) delete draft.itemsById[deleted.id];
        }
      }),
    [LOG_OUT]: () => initialState,
  },
  initialState
);
